import React, { useState, useEffect, useMemo } from 'react'
import {
  ShoppingCartIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  EyeIcon,
  FunnelIcon,
  ArrowPathIcon,
  PrinterIcon,
  TrashIcon,
  CalendarIcon,
  ArrowDownTrayIcon,
  PencilIcon,
  XMarkIcon,
  CurrencyDollarIcon,
  ChartBarIcon,
  UserGroupIcon,
  CheckIcon
} from '@heroicons/react/24/outline'
import * as XLSX from 'xlsx'
import { apiService, formatItemName, consolidateOrderItems, deduplicateOrderItems } from '../services/api'
import { useAuth } from '../contexts/AuthContext'
import ThermalReceipt from '../components/Receipt/ThermalReceipt'
import { splitVatInclusive } from '../utils/tax'

interface OrderItem {
  id: string
  item_name: string
  item_description?: string
  base_price: number
  variant_name?: string
  variant_price_modifier: number
  addons: any[]
  quantity: number
  unit_price: number
  total_price: number
  notes?: string
  status: 'pending' | 'preparing' | 'ready' | 'completed' | 'cancelled' | 'refunded'
  kds_station_id?: string
  kds_stations?: {
    id: string
    name: string
    color: string
  }
}

interface Order {
  id: string
  order_number: string
  token_number?: number
  customer_id?: string
  customer_name?: string
  customer_phone?: string
  order_type: 'dine_in' | 'takeaway' | 'catering'
  table_number?: string
  status: 'pending' | 'confirmed' | 'preparing' | 'ready' | 'completed' | 'cancelled' | 'refunded'
  subtotal: number
  tax_amount: number
  service_charge: number
  discount_amount: number
  total_amount: number
  total?: number // alternate field from API
  payment_method?: string
  payment_status: 'pending' | 'paid' | 'failed' | 'refunded'
  paid_amount: number
  change_amount: number
  is_split_payment?: boolean
  split_payment_details?: {
    total_payments: number
    cash_amount: number
    card_amount: number
    digital_amount: number
    total_change: number
    payments: Array<{
      payment_method: string
      amount: number
      received_amount?: number
      change_amount?: number
    }>
  }
  applied_offers: any[]
  applied_visit_based_offers?: any[]
  notes?: string
  preparation_time?: number
  actual_preparation_time?: number
  order_time: string
  confirmed_time?: string
  ready_time?: string
  completed_time?: string
  created_at: string
  updated_at: string
  order_items: OrderItem[]
  /** Per-line items for display (no KDS quantity summing); use in Order Details so qty matches what was ordered */
  order_items_for_modification?: OrderItem[]
  customers?: {
    id: string
    name: string
    phone_number: string
  }
  created_by?: string | null
  placed_by_name?: string | null
}

interface RestaurantSettings {
  restaurant_name: string
  phone_number: string
  address: string
  email: string
  website: string
  currency: string
  currency_symbol: string
  currency_display_type: 'text' | 'icon'
  currency_display_position: 'left' | 'right'
  currency_icon_url: string
  timezone: string
  language: string
  tax_rate: number
  service_charge: number
}

const Orders: React.FC = () => {
  const { user } = useAuth()
  const [orders, setOrders] = useState<Order[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null)
  const [showOrderModal, setShowOrderModal] = useState(false)
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [typeFilter, setTypeFilter] = useState<string>('all')
  const [dateRangeFilter, setDateRangeFilter] = useState<'today' | 'yesterday' | 'week' | 'yearly' | 'custom'>('today')
  const [showCustomDateRange, setShowCustomDateRange] = useState(false)
  const [customStartDate, setCustomStartDate] = useState<string>('')
  const [customEndDate, setCustomEndDate] = useState<string>('')
  const [receiptData, setReceiptData] = useState<Order | null>(null)
  const [deletingOrderId, setDeletingOrderId] = useState<string | null>(null)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [orderToDelete, setOrderToDelete] = useState<{ id: string; orderNumber: string } | null>(null)
  const [selectedOrders, setSelectedOrders] = useState<Set<string>>(new Set())
  const [editingStatusOrderId, setEditingStatusOrderId] = useState<string | null>(null)
  const [updatingStatusOrderId, setUpdatingStatusOrderId] = useState<string | null>(null)
  const [showBulkStatusModal, setShowBulkStatusModal] = useState(false)
  const [bulkStatusValue, setBulkStatusValue] = useState<string>('')
  const [updatingBulkStatus, setUpdatingBulkStatus] = useState(false)
  
  // Check if user is Super Admin or Admin
  const isSuperAdmin = user?.roles?.some(r => r.name === 'Super Admin') || false
  const isAdmin = user?.roles?.some(r => r.name === 'Admin') || false
  const canEditOrderStatus = isSuperAdmin || isAdmin
  const [posSettings, setPOSSettings] = useState<any>({
    enableAutoPrint: false,
    printerType: 'tcp_ip',
    printerIp: '',
    printerPort: 9100,
    printerWidth: 80,
    receiptLogoUrl: '',
    receiptLayout: []
  })
  const [restaurantSettings, setRestaurantSettings] = useState<RestaurantSettings>({
    restaurant_name: 'Mufaza Restaurant',
    phone_number: '+966 11 123 4567',
    address: '123 King Fahd Road, Riyadh, Saudi Arabia',
    email: 'info@mufaza.com',
    website: 'www.mufaza.com',
    currency: 'SAR',
    currency_symbol: 'ر.س',
    currency_display_type: 'text',
    currency_display_position: 'right',
    currency_icon_url: '',
    timezone: 'Asia/Riyadh',
    language: 'en',
    tax_rate: 15,
    service_charge: 0
  })

  useEffect(() => {
    loadOrders(true) // Show loading on initial load
    loadRestaurantSettings()
    loadPOSSettings()
  }, [dateRangeFilter, customStartDate, customEndDate])
  
  useEffect(() => {
    // Auto-refresh orders every 10 seconds to get latest status updates
    // Don't show loading state during auto-refresh to avoid glitch
    const interval = setInterval(() => {
      loadOrders(false) // Don't show loading during auto-refresh
    }, 10000)
    
    return () => clearInterval(interval)
  }, [dateRangeFilter, customStartDate, customEndDate])

  const loadPOSSettings = async () => {
    try {
      const response = await apiService.getPOSSettings()
      if (response.success && response.data) {
        const posData = response.data as any
        
        // Ensure receiptLayout is properly parsed if it's a string
        if (posData.receiptLayout && typeof posData.receiptLayout === 'string') {
          try {
            posData.receiptLayout = JSON.parse(posData.receiptLayout)
          } catch (e) {
            console.error('Error parsing receipt layout:', e)
            posData.receiptLayout = []
          }
        }
        
        setPOSSettings(posData)
      }
    } catch (error) {
      console.error('Error loading POS settings:', error)
    }
  }

  const loadRestaurantSettings = async () => {
    try {
      const response = await apiService.getRestaurantSettings()
      if (response.success && response.data) {
        setRestaurantSettings(response.data as any)
      }
    } catch (error) {
      console.error('Error loading restaurant settings:', error)
    }
  }

  const getDateRange = () => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    
    switch (dateRangeFilter) {
      case 'today':
        return {
          start: new Date(today),
          end: new Date(today.getTime() + 24 * 60 * 60 * 1000) // End of today
        }
      case 'yesterday':
        const yesterday = new Date(today)
        yesterday.setDate(yesterday.getDate() - 1)
        return {
          start: yesterday,
          end: new Date(yesterday.getTime() + 24 * 60 * 60 * 1000)
        }
      case 'week':
        const weekStart = new Date(today)
        weekStart.setDate(weekStart.getDate() - weekStart.getDay()) // Start of week (Sunday)
        return {
          start: weekStart,
          end: new Date(today.getTime() + 24 * 60 * 60 * 1000) // End of today
        }
      case 'yearly':
        const yearStart = new Date(today.getFullYear(), 0, 1) // January 1st
        return {
          start: yearStart,
          end: new Date(today.getTime() + 24 * 60 * 60 * 1000) // End of today
        }
      case 'custom':
        if (customStartDate && customEndDate) {
          const start = new Date(customStartDate)
          start.setHours(0, 0, 0, 0)
          const end = new Date(customEndDate)
          end.setHours(23, 59, 59, 999)
          return { start, end }
        }
        return null
      default:
        return null
    }
  }

  const loadOrders = async (showLoading = true) => {
    if (showLoading) {
      setLoading(true)
    }
    try {
      const useBackendDateFilter = dateRangeFilter === 'today' || dateRangeFilter === 'yesterday'
      const response = await apiService.getOrders(
        useBackendDateFilter ? { date_range: dateRangeFilter } : undefined
      )
      if (response.success && response.data) {
        let ordersData = Array.isArray(response.data) ? response.data : []
        if (!useBackendDateFilter) {
          const dateRange = getDateRange()
          if (dateRange) {
            ordersData = ordersData.filter((order: Order) => {
              const orderDate = new Date(order.order_time)
              return orderDate >= dateRange.start && orderDate <= dateRange.end
            })
          }
        }
        setOrders(ordersData)
      }
    } catch (error) {
      console.error('Error loading orders:', error)
    } finally {
      if (showLoading) {
        setLoading(false)
      }
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
      case 'confirmed':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
      case 'preparing':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200'
      case 'ready':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
      case 'completed':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
      case 'cancelled':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <ClockIcon className="h-4 w-4" />
      case 'confirmed':
        return <CheckCircleIcon className="h-4 w-4" />
      case 'preparing':
        return <ClockIcon className="h-4 w-4" />
      case 'ready':
        return <CheckCircleIcon className="h-4 w-4" />
      case 'completed':
        return <CheckCircleIcon className="h-4 w-4" />
      case 'cancelled':
        return <XCircleIcon className="h-4 w-4" />
      default:
        return <ClockIcon className="h-4 w-4" />
    }
  }

  const formatCurrency = (amount: number) => {
    if (!restaurantSettings || !restaurantSettings.currency) {
      return `ر.س ${amount.toFixed(2)}`;
    }
    const { currency, currency_symbol } = restaurantSettings;
    const currency_symbol_display = (restaurantSettings as any).currency_symbol_display || 'text';
    const currency_symbol_position = (restaurantSettings as any).currency_display_position || 'left';
    const symbol = currency_symbol_display === 'icon' && currency_symbol ? currency_symbol : currency;
    const formattedAmount = new Intl.NumberFormat('en-US', {
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);

    if (currency_symbol_position === 'left') {
      return `${symbol} ${formattedAmount}`;
    } else {
      return `${formattedAmount} ${symbol}`;
    }
  }

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  // Some historical/edge rows can have total_price as 0 while unit_price is populated.
  // Derive a stable display total so card and details always reflect billable value.
  const getOrderItemLineTotal = (item: Partial<OrderItem> | any): number => {
    const qty = Math.max(1, Number(item?.quantity) || 1)
    const totalPrice = Number(item?.total_price)
    const unitPrice = Number(item?.unit_price)
    const hasValidTotal = Number.isFinite(totalPrice)
    const hasValidUnit = Number.isFinite(unitPrice)

    if (hasValidTotal && totalPrice > 0) return totalPrice
    if (hasValidUnit && unitPrice > 0) return unitPrice * qty
    if (hasValidTotal) return totalPrice
    return 0
  }

  /** Line count for CSV/Excel — matches Order Details (order_items_for_modification when present; excludes non-cancelled item rows when order is not cancelled). */
  const getExportOrderLineCount = (order: Order): number => {
    const sourceItems = (order.order_items_for_modification || order.order_items || [])
    const items = order.status === 'cancelled'
      ? sourceItems
      : sourceItems.filter((item: any) => item.status !== 'cancelled')
    return items.length
  }

  /** Total for an order computed from its items (so grid card matches item list); returns undefined if no items */
  const getOrderTotalFromItems = (order: Order): number | undefined => {
    const sourceItems = (order.order_items_for_modification || order.order_items || [])
    const items = order.status === 'cancelled'
      ? sourceItems
      : sourceItems.filter((item: any) => item.status !== 'cancelled')
    if (items.length === 0) return undefined
    const subtotal = items.reduce((sum: number, item: any) => sum + getOrderItemLineTotal(item), 0)
    const discount = Number(order.discount_amount) || 0
    const serviceCharge = Number(order.service_charge) || 0
    return Math.max(0, subtotal - discount) + serviceCharge
  }

  /** Order Summary computed from displayed items so totals always match the item list */
  const orderModalSummary = useMemo(() => {
    if (!selectedOrder) return null
    const sourceItems = (selectedOrder.order_items_for_modification || selectedOrder.order_items || [])
    const items = selectedOrder.status === 'cancelled'
      ? sourceItems
      : sourceItems.filter((item: any) => item.status !== 'cancelled')
    const subtotalInclVat = items.reduce((sum: number, item: any) => sum + getOrderItemLineTotal(item), 0)
    const discountAmount = Number(selectedOrder.discount_amount) || 0
    const afterDiscount = Math.max(0, subtotalInclVat - discountAmount)
    const taxRateDecimal = (Number(restaurantSettings?.tax_rate) || 15) / 100
    const { vat } = splitVatInclusive(afterDiscount, taxRateDecimal)
    const serviceCharge = Number(selectedOrder.service_charge) || 0
    const total = afterDiscount + serviceCharge
    return {
      subtotalInclVat,
      discountAmount,
      vat,
      serviceCharge,
      total
    }
  }, [selectedOrder, restaurantSettings?.tax_rate])

  /** Build receipt payload so print values exactly match Order Details modal (VAT-inclusive). */
  const getPrintableOrderFromModal = (order: Order): Order => {
    const sourceItems = (order.order_items_for_modification || order.order_items || [])
    const rawItems = order.status === 'cancelled'
      ? sourceItems
      : sourceItems.filter((item: any) => item.status !== 'cancelled')
    // Match backend receipt / ESC/POS: keep separate "additional items" lines (time-bucketed), not consolidateOrderItems(false) which drops duplicate keys.
    const displayItems = deduplicateOrderItems(rawItems as any, false, false)
    const normalizedDisplayItems = displayItems.map((item: any) => ({
      ...item,
      total_price: getOrderItemLineTotal(item)
    }))
    const subtotalInclVat = normalizedDisplayItems.reduce((sum: number, item: any) => sum + getOrderItemLineTotal(item), 0)
    const discountAmount = Number(order.discount_amount) || 0
    const afterDiscount = Math.max(0, subtotalInclVat - discountAmount)
    const taxRateDecimal = (Number(restaurantSettings?.tax_rate) || 15) / 100
    const { vat } = splitVatInclusive(afterDiscount, taxRateDecimal)
    const serviceCharge = Number(order.service_charge) || 0
    const total = afterDiscount + serviceCharge

    return {
      ...order,
      order_items: normalizedDisplayItems as any,
      subtotal: subtotalInclVat,
      tax_amount: vat,
      total_amount: total
    }
  }

  const filteredOrders = orders.filter(order => {
    const statusMatch = statusFilter === 'all' || order.status === statusFilter
    // Map order_type to filter values
    let orderTypeMatch = true
    if (typeFilter !== 'all') {
      if (typeFilter === 'dine_in') {
        orderTypeMatch = order.order_type === 'dine_in'
      } else if (typeFilter === 'takeaway') {
        orderTypeMatch = order.order_type === 'takeaway'
      } else if (typeFilter === 'catering') {
        orderTypeMatch = order.order_type === 'catering'
      }
    }
    return statusMatch && orderTypeMatch
  })

  const openOrderModal = (order: Order) => {
    setSelectedOrder(order)
    setShowOrderModal(true)
  }

  const openDeleteModal = (orderId: string, orderNumber: string) => {
    if (!isSuperAdmin) {
      return
    }
    setOrderToDelete({ id: orderId, orderNumber })
    setShowDeleteModal(true)
  }

  const handleDeleteOrder = async () => {
    if (!orderToDelete || !isSuperAdmin) return

    setDeletingOrderId(orderToDelete.id)
    try {
      const response = await apiService.deleteOrder(orderToDelete.id, user?.id)
      if (response.success) {
        // Remove the order from the list
        setOrders(prevOrders => prevOrders.filter(o => o.id !== orderToDelete.id))
        // Close modals if the deleted order was selected
        if (selectedOrder?.id === orderToDelete.id) {
          setShowOrderModal(false)
          setSelectedOrder(null)
        }
        setShowDeleteModal(false)
        setOrderToDelete(null)
      } else {
        alert(`Failed to delete order: ${response.error || 'Unknown error'}`)
      }
    } catch (error) {
      console.error('Error deleting order:', error)
      alert('An error occurred while deleting the order')
    } finally {
      setDeletingOrderId(null)
    }
  }

  const getFilteredOrders = () => {
    let filtered = [...orders]

    // Filter by status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(order => order.status === statusFilter)
    }

    // Filter by type
    if (typeFilter !== 'all') {
      filtered = filtered.filter(order => order.order_type === typeFilter)
    }

    return filtered
  }

  // Calculate summary metrics
  const summaryMetrics = useMemo(() => {
    const filteredOrders = getFilteredOrders()
    
    // Order count
    const orderCount = filteredOrders.length
    
    // Order value (total revenue) - use total_amount or total, exclude cancelled
    const orderValue = filteredOrders
      .filter((order) => order.status !== 'cancelled')
      .reduce((sum, order) => sum + (Number(order.total_amount ?? order.total ?? 0) || 0), 0)
    
    // Top item - use per-line items when available so KDS duplicates don't over-count; then consolidate by item name across orders
    const itemCounts: Record<string, { name: string; count: number }> = {}
    filteredOrders.forEach(order => {
      const rawItems = (order.order_items_for_modification || order.order_items || []).filter(
        (item: any) => item.status !== 'cancelled'
      )
      const consolidatedItems = consolidateOrderItems(rawItems)
      consolidatedItems.forEach(item => {
        const itemName = item.item_name || 'Unknown'
        if (itemCounts[itemName]) {
          itemCounts[itemName].count += item.quantity || 1
        } else {
          itemCounts[itemName] = { name: itemName, count: item.quantity || 1 }
        }
      })
    })
    const topItem = Object.values(itemCounts).sort((a, b) => b.count - a.count)[0] || null
    
    // Footfall: unique visits (exclude cancelled); same customer = 1, no customer = 1 per order
    const footfallSet = new Set<string>()
    filteredOrders
      .filter(order => order.status !== 'cancelled')
      .forEach(order => {
        const key = order.customer_id ?? order.customer_phone ?? order.id
        if (key) footfallSet.add(String(key))
      })
    const footfall = footfallSet.size
    
    return {
      orderCount,
      orderValue,
      topItem: topItem ? `${topItem.name} (${topItem.count}x)` : 'N/A',
      footfall
    }
  }, [orders, statusFilter, typeFilter, dateRangeFilter, customStartDate, customEndDate])

  const handleSelectOrder = (orderId: string) => {
    setSelectedOrders(prev => {
      const newSet = new Set(prev)
      if (newSet.has(orderId)) {
        newSet.delete(orderId)
      } else {
        newSet.add(orderId)
      }
      return newSet
    })
  }

  const handleSelectAll = () => {
    const filteredOrders = getFilteredOrders()
    if (selectedOrders.size === filteredOrders.length) {
      setSelectedOrders(new Set())
    } else {
      setSelectedOrders(new Set(filteredOrders.map(o => o.id)))
    }
  }

  const handleSingleStatusUpdate = async (orderId: string, newStatus: string) => {
    setUpdatingStatusOrderId(orderId)
    try {
      const response = await apiService.updateOrder(orderId, { status: newStatus })
      if (response.success) {
        // Update the order in the local state
        setOrders(prevOrders =>
          prevOrders.map(order =>
            order.id === orderId ? { ...order, status: newStatus as Order['status'] } : order
          )
        )
        setEditingStatusOrderId(null)
      } else {
        alert(`Failed to update order status: ${response.error || 'Unknown error'}`)
      }
    } catch (error) {
      console.error('Error updating order status:', error)
      alert('An error occurred while updating order status')
    } finally {
      setUpdatingStatusOrderId(null)
    }
  }

  const handleBulkStatusUpdate = async () => {
    if (selectedOrders.size === 0 || !bulkStatusValue) {
      return
    }

    setUpdatingBulkStatus(true)
    try {
      const updatePromises = Array.from(selectedOrders).map(orderId =>
        apiService.updateOrder(orderId, { status: bulkStatusValue })
      )

      const results = await Promise.all(updatePromises)
      const failed = results.filter(r => !r.success)

      if (failed.length === 0) {
        // Update all orders in local state
        setOrders(prevOrders =>
          prevOrders.map(order =>
            selectedOrders.has(order.id)
              ? { ...order, status: bulkStatusValue as Order['status'] }
              : order
          )
        )
        setSelectedOrders(new Set())
        setShowBulkStatusModal(false)
        setBulkStatusValue('')
      } else {
        alert(`Failed to update ${failed.length} order(s). Please try again.`)
      }
    } catch (error) {
      console.error('Error updating bulk order status:', error)
      alert('An error occurred while updating order statuses')
    } finally {
      setUpdatingBulkStatus(false)
    }
  }

  const exportToCSV = () => {
    const filteredOrders = getFilteredOrders()
    
    if (filteredOrders.length === 0) {
      alert('No orders to export')
      return
    }

    // Prepare CSV data
    const headers = [
      'Order Number',
      'Date & Time',
      'Customer Name',
      'Phone',
      'Order Type',
      'Status',
      'Payment Method',
      'Payment Status',
      'Subtotal',
      'Tax',
      'Discount',
      'Total',
      'Items Count'
    ]

    const rows = filteredOrders.map(order => [
      order.order_number || order.id,
      formatDateTime(order.order_time),
      order.customer_name || 'Guest',
      order.customer_phone || '-',
      order.order_type === 'dine_in' ? 'Dine In' : order.order_type === 'takeaway' ? 'Takeaway' : 'Catering',
      order.status.charAt(0).toUpperCase() + order.status.slice(1),
      order.payment_method ? order.payment_method.charAt(0).toUpperCase() + order.payment_method.slice(1) : '-',
      order.payment_status ? order.payment_status.charAt(0).toUpperCase() + order.payment_status.slice(1) : '-',
      formatCurrency(order.subtotal || 0).replace(/[^\d.,-]/g, ''),
      formatCurrency(order.tax_amount || 0).replace(/[^\d.,-]/g, ''),
      formatCurrency(order.discount_amount || 0).replace(/[^\d.,-]/g, ''),
      formatCurrency(order.total_amount || 0).replace(/[^\d.,-]/g, ''),
      getExportOrderLineCount(order)
    ])

    // Create CSV content
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n')

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    
    const dateRangeLabel = dateRangeFilter === 'custom' && customStartDate && customEndDate
      ? `${customStartDate}_to_${customEndDate}`
      : dateRangeFilter
    link.setAttribute('download', `Orders_${dateRangeLabel}_${new Date().toISOString().split('T')[0]}.csv`)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const exportToExcel = () => {
    const filteredOrders = getFilteredOrders()
    
    if (filteredOrders.length === 0) {
      alert('No orders to export')
      return
    }

    const workbook = XLSX.utils.book_new()
    
    // Prepare data
    const headers = [
      'Order Number',
      'Date & Time',
      'Customer Name',
      'Phone',
      'Order Type',
      'Status',
      'Payment Method',
      'Payment Status',
      'Subtotal',
      'Tax',
      'Discount',
      'Total',
      'Items Count'
    ]

    const rows = filteredOrders.map(order => [
      order.order_number || order.id,
      formatDateTime(order.order_time),
      order.customer_name || 'Guest',
      order.customer_phone || '-',
      order.order_type === 'dine_in' ? 'Dine In' : order.order_type === 'takeaway' ? 'Takeaway' : 'Catering',
      order.status.charAt(0).toUpperCase() + order.status.slice(1),
      order.payment_method ? order.payment_method.charAt(0).toUpperCase() + order.payment_method.slice(1) : '-',
      order.payment_status ? order.payment_status.charAt(0).toUpperCase() + order.payment_status.slice(1) : '-',
      order.subtotal || 0,
      order.tax_amount || 0,
      order.discount_amount || 0,
      order.total_amount || 0,
      getExportOrderLineCount(order)
    ])

    const data = [headers, ...rows]
    const worksheet = XLSX.utils.aoa_to_sheet(data)
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Orders')

    // Generate filename
    const dateRangeLabel = dateRangeFilter === 'custom' && customStartDate && customEndDate
      ? `${customStartDate}_to_${customEndDate}`
      : dateRangeFilter
    const fileName = `Orders_${dateRangeLabel}_${new Date().toISOString().split('T')[0]}.xlsx`

    XLSX.writeFile(workbook, fileName)
  }

  return (
    <div className="py-6 -mx-6 lg:-mx-8 px-4 lg:px-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <ShoppingCartIcon className="h-8 w-8 text-blue-600" />
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Orders</h1>
        </div>
        <button
          onClick={() => loadOrders(true)}
          disabled={loading}
          className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
        >
          <ArrowPathIcon className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          {loading ? 'Loading...' : 'Refresh'}
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {/* Order Count Card */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Order Count</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {summaryMetrics.orderCount}
              </p>
            </div>
            <div className="bg-blue-100 dark:bg-blue-900/20 rounded-full p-3">
              <ShoppingCartIcon className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
          </div>
        </div>

        {/* Order Value Card */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Order Value</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {formatCurrency(Number(summaryMetrics.orderValue) || 0)}
              </p>
            </div>
            <div className="bg-green-100 dark:bg-green-900/20 rounded-full p-3">
              <CurrencyDollarIcon className="h-6 w-6 text-green-600 dark:text-green-400" />
            </div>
          </div>
        </div>

        {/* Top Item Card */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Top Item</p>
              <p className="text-lg font-semibold text-gray-900 dark:text-white mt-1 truncate">
                {summaryMetrics.topItem}
              </p>
            </div>
            <div className="bg-purple-100 dark:bg-purple-900/20 rounded-full p-3 ml-2">
              <ChartBarIcon className="h-6 w-6 text-purple-600 dark:text-purple-400" />
            </div>
          </div>
        </div>

        {/* Footfall Card */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Footfall</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {summaryMetrics.footfall}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Customers + Takeaway
              </p>
            </div>
            <div className="bg-orange-100 dark:bg-orange-900/20 rounded-full p-3">
              <UserGroupIcon className="h-6 w-6 text-orange-600 dark:text-orange-400" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-6 space-y-4">
        <div className="flex items-center space-x-2">
          <FunnelIcon className="h-5 w-5 text-gray-500" />
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Filters:</span>
        </div>
        
        {/* Date Range Shortcuts and Status/Type Filters on Same Line */}
        <div className="flex flex-wrap items-center gap-3">
          {/* Date Range Shortcuts */}
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => {
                setDateRangeFilter('today')
                setShowCustomDateRange(false)
              }}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                dateRangeFilter === 'today'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
              }`}
            >
              Today
            </button>
            <button
              onClick={() => {
                setDateRangeFilter('yesterday')
                setShowCustomDateRange(false)
              }}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                dateRangeFilter === 'yesterday'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
              }`}
            >
              Yesterday
            </button>
            <button
              onClick={() => {
                setDateRangeFilter('week')
                setShowCustomDateRange(false)
              }}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                dateRangeFilter === 'week'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
              }`}
            >
              This Week
            </button>
            <button
              onClick={() => {
                setDateRangeFilter('yearly')
                setShowCustomDateRange(false)
              }}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                dateRangeFilter === 'yearly'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
              }`}
            >
              This Year
            </button>
            <button
              onClick={() => {
                setDateRangeFilter('custom')
                setShowCustomDateRange(true)
              }}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center space-x-2 ${
                dateRangeFilter === 'custom'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
              }`}
            >
              <CalendarIcon className="h-4 w-4" />
              <span>Custom Range</span>
            </button>
          </div>

          {/* Divider */}
          <div className="h-6 w-px bg-gray-300 dark:bg-gray-600"></div>

          {/* Status and Type Filters */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="confirmed">Confirmed</option>
            <option value="preparing">Preparing</option>
            <option value="ready">Ready</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>

          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
          >
            <option value="all">All Types</option>
            <option value="dine_in">Dine In</option>
            <option value="takeaway">Takeaway</option>
            <option value="catering">Catering</option>
          </select>

          {/* Export Buttons */}
          <div className="ml-auto flex items-center gap-2">
            <div className="h-6 w-px bg-gray-300 dark:bg-gray-600"></div>
            <button
              onClick={exportToCSV}
              className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              title="Export to CSV"
            >
              <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
              CSV
            </button>
            <button
              onClick={exportToExcel}
              className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              title="Export to Excel"
            >
              <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
              Excel
            </button>
          </div>
        </div>

        {/* Custom Date Range Inputs */}
        {showCustomDateRange && dateRangeFilter === 'custom' && (
          <div className="flex flex-wrap gap-3 items-center bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">From:</span>
            <input
              type="date"
              value={customStartDate}
              onChange={(e) => setCustomStartDate(e.target.value)}
              className="border border-gray-300 dark:border-gray-600 rounded-md px-3 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
            />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">To:</span>
            <input
              type="date"
              value={customEndDate}
              onChange={(e) => setCustomEndDate(e.target.value)}
              min={customStartDate || undefined}
              className="border border-gray-300 dark:border-gray-600 rounded-md px-3 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
            />
            <button
              onClick={() => {
                setDateRangeFilter('today')
                setShowCustomDateRange(false)
                setCustomStartDate('')
                setCustomEndDate('')
              }}
              className="px-3 py-1 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
            >
              Cancel
            </button>
          </div>
        )}
      </div>

      {/* Bulk Actions Bar */}
      {selectedOrders.size > 0 && canEditOrderStatus && (
        <div className="mb-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <span className="text-sm font-medium text-blue-900 dark:text-blue-200">
                {selectedOrders.size} order{selectedOrders.size !== 1 ? 's' : ''} selected
              </span>
              <button
                onClick={() => setShowBulkStatusModal(true)}
                className="inline-flex items-center px-3 py-1.5 border border-blue-300 dark:border-blue-700 rounded-md text-sm font-medium text-blue-700 dark:text-blue-300 bg-white dark:bg-gray-800 hover:bg-blue-50 dark:hover:bg-gray-700"
              >
                <PencilIcon className="h-4 w-4 mr-2" />
                Update Status
              </button>
            </div>
            <button
              onClick={() => setSelectedOrders(new Set())}
              className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
            >
              Clear Selection
            </button>
          </div>
        </div>
      )}

      {/* Orders List */}
      {loading ? (
        <div className="text-center py-12">
          <ArrowPathIcon className="h-12 w-12 text-gray-400 mx-auto mb-4 animate-spin" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            Loading orders...
          </h3>
          <p className="text-gray-500 dark:text-gray-400">
            Please wait while we fetch the latest orders
          </p>
        </div>
      ) : (
        <div>
          {/* Select All Button */}
          {filteredOrders.length > 0 && (
            <div className="mb-4">
              <button
                onClick={handleSelectAll}
                className={`inline-flex items-center px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  selectedOrders.size === filteredOrders.length && filteredOrders.length > 0
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                }`}
              >
                {selectedOrders.size === filteredOrders.length && filteredOrders.length > 0 ? (
                  <>
                    <CheckIcon className="h-4 w-4 mr-2" />
                    All Selected ({filteredOrders.length})
                  </>
                ) : (
                  <>
                    Select All ({filteredOrders.length} orders)
                  </>
                )}
              </button>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-3 xl:grid-cols-4 gap-4 lg:gap-6">
            {filteredOrders.map((order) => (
            <div
              key={order.id}
              onClick={() => handleSelectOrder(order.id)}
              className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm border p-6 hover:shadow-md transition-all cursor-pointer ${
                selectedOrders.has(order.id)
                  ? 'border-blue-500 dark:border-blue-400 ring-2 ring-blue-200 dark:ring-blue-800 bg-blue-50 dark:bg-blue-900/10'
                  : 'border-gray-200 dark:border-gray-700'
              }`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-1">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      #{order.order_number}
                    </h3>
                    {selectedOrders.has(order.id) && (
                      <div className="flex items-center justify-center w-5 h-5 bg-blue-600 dark:bg-blue-500 rounded text-white">
                        <CheckIcon className="h-3 w-3" />
                      </div>
                    )}
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {formatDateTime(order.order_time)}
                  </p>
                </div>
                <div className="flex flex-col items-end gap-2">
                  {editingStatusOrderId === order.id ? (
                    <div className="flex items-center space-x-1">
                      <select
                        value={order.status}
                        onChange={(e) => handleSingleStatusUpdate(order.id, e.target.value)}
                        disabled={updatingStatusOrderId === order.id}
                        className="text-xs border border-gray-300 dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        autoFocus
                      >
                        <option value="pending">Pending</option>
                        <option value="confirmed">Confirmed</option>
                        <option value="preparing">Preparing</option>
                        <option value="ready">Ready</option>
                        <option value="completed">Completed</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          setEditingStatusOrderId(null)
                        }}
                        className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                        title="Cancel"
                      >
                        <XMarkIcon className="h-3 w-3" />
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-1">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(order.status)}`}>
                        {getStatusIcon(order.status)}
                        <span className="ml-1 capitalize">{order.status}</span>
                      </span>
                      {canEditOrderStatus && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            setEditingStatusOrderId(order.id)
                          }}
                          className="p-1 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400"
                          title="Edit Status"
                        >
                          <PencilIcon className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                  )}
                  {order.token_number !== undefined && order.token_number !== null && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 text-xs font-semibold">
                      Token #{order.token_number}
                    </span>
                  )}
                </div>
              </div>

              <div className="space-y-2 mb-4">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500 dark:text-gray-400">Customer:</span>
                  <span className="text-gray-900 dark:text-white font-medium">
                    {order.customer_name || 'Guest'}
                  </span>
                </div>
                {(order.customer_phone || order.customers?.phone_number) && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500 dark:text-gray-400">Phone:</span>
                    <span className="text-gray-900 dark:text-white">
                      {order.customer_phone || order.customers?.phone_number}
                    </span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500 dark:text-gray-400">Type:</span>
                  <span className="text-gray-900 dark:text-white capitalize">
                    {order.order_type.replace('_', ' ')}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500 dark:text-gray-400">Placed by:</span>
                  <span className="text-gray-900 dark:text-white font-medium">
                    {order.placed_by_name || '—'}
                  </span>
                </div>
                {order.table_number && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500 dark:text-gray-400">Table:</span>
                    <span className="text-gray-900 dark:text-white font-medium">
                      {order.table_number}
                    </span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500 dark:text-gray-400">Items:</span>
                  <span className="text-gray-900 dark:text-white">
                    {(() => {
                      const sourceItems = (order.order_items_for_modification || order.order_items || [])
                      const rawItems = order.status === 'cancelled'
                        ? sourceItems
                        : sourceItems.filter((item: any) => item.status !== 'cancelled')
                      const n = rawItems.length
                      return `${n} item${n !== 1 ? 's' : ''}`
                    })()}
                  </span>
                </div>
                <div className="flex justify-between text-sm font-semibold">
                  <span className="text-gray-500 dark:text-gray-400">Total:</span>
                  <span className="text-gray-900 dark:text-white">
                    {formatCurrency(getOrderTotalFromItems(order) ?? order.total_amount ?? order.total ?? 0)}
                  </span>
                </div>
              </div>

              <div className="flex space-x-2">
                <button
                  onClick={() => openOrderModal(order)}
                  className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                >
                  <EyeIcon className="h-4 w-4 mr-2" />
                  View Details
                </button>
                {isSuperAdmin && (
                  <button
                    onClick={() => openDeleteModal(order.id, order.order_number)}
                    disabled={deletingOrderId === order.id}
                    className="inline-flex items-center justify-center px-3 py-2 rounded-md text-sm font-medium text-white bg-red-600 dark:bg-red-700 hover:bg-red-700 dark:hover:bg-red-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    title="Delete Order (Super Admin Only)"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          ))}

          {filteredOrders.length === 0 && (
            <div className="col-span-full text-center py-12">
              <ShoppingCartIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                No orders found
              </h3>
              <p className="text-gray-500 dark:text-gray-400">
                {statusFilter !== 'all' || typeFilter !== 'all' 
                  ? 'Try adjusting your filters to see more orders'
                  : 'Orders will appear here when customers place them'
                }
              </p>
            </div>
          )}
          </div>
        </div>
      )}

      {/* Order Details Modal */}
      {showOrderModal && selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                  Order #{selectedOrder.order_number}
                </h3>
                {selectedOrder.token_number !== undefined && selectedOrder.token_number !== null && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 text-xs font-semibold mt-2">
                    Token #{selectedOrder.token_number}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                {isSuperAdmin && (
                  <button
                    onClick={() => {
                      if (selectedOrder) {
                        openDeleteModal(selectedOrder.id, selectedOrder.order_number)
                      }
                    }}
                    disabled={deletingOrderId === selectedOrder?.id}
                    className="inline-flex items-center px-3 py-2 rounded-md text-sm font-medium text-white bg-red-600 dark:bg-red-700 hover:bg-red-700 dark:hover:bg-red-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    title="Delete Order (Super Admin Only)"
                  >
                    <TrashIcon className="h-4 w-4 mr-2" />
                    Delete Order
                  </button>
                )}
                <button
                  onClick={() => {
                    if (!selectedOrder) return
                    const printableOrder = getPrintableOrderFromModal(selectedOrder)
                    // Check if auto print is enabled and TCP/IP printer is configured
                    if (posSettings.enableAutoPrint && posSettings.printerType === 'tcp_ip' && posSettings.printerIp) {
                      // Print directly to TCP/IP printer via backend
                      const printerConfig = {
                        printerIp: posSettings.printerIp,
                        printerPort: posSettings.printerPort || 9100,
                        printerName: posSettings.printerName,
                        printerModel: posSettings.printerModel
                      }
                      
                      apiService.printReceipt(
                        printableOrder,
                        restaurantSettings,
                        printerConfig,
                        posSettings.receiptLayout,
                        posSettings.printerWidth || 80,
                        posSettings.receiptLogoUrl
                      )
                        .then((printResponse) => {
                          if (printResponse.success) {
                            // Optionally show success message
                          } else {
                            const msg = printResponse?.error || 'Print failed'
                            console.error('Failed to print receipt:', msg)
                            alert(msg)
                            setReceiptData(printableOrder)
                          }
                        })
                        .catch((error) => {
                          const msg = error?.message || error?.error || 'Print failed'
                          console.error('Print error:', error)
                          alert(msg)
                          setReceiptData(printableOrder)
                        })
                    } else {
                      // Show browser print dialog (auto print disabled or not TCP/IP)
                      setReceiptData(printableOrder)
                    }
                  }}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                >
                  <PrinterIcon className="h-4 w-4 mr-2" />
                  Print Receipt
                </button>
                <button
                  onClick={() => setShowOrderModal(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <XCircleIcon className="h-6 w-6" />
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Order Information */}
              <div className="space-y-4">
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-3">Order Information</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500 dark:text-gray-400">Status:</span>
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(selectedOrder.status)}`}>
                        {getStatusIcon(selectedOrder.status)}
                        <span className="ml-1 capitalize">{selectedOrder.status}</span>
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500 dark:text-gray-400">Type:</span>
                      <span className="text-gray-900 dark:text-white capitalize">
                        {selectedOrder.order_type.replace('_', ' ')}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500 dark:text-gray-400">Order Time:</span>
                      <span className="text-gray-900 dark:text-white">
                        {formatDateTime(selectedOrder.order_time)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500 dark:text-gray-400">Placed by:</span>
                      <span className="text-gray-900 dark:text-white font-medium">
                        {selectedOrder.placed_by_name || '—'}
                      </span>
                    </div>
                    {selectedOrder.table_number && (
                      <div className="flex justify-between">
                        <span className="text-gray-500 dark:text-gray-400">Table:</span>
                        <span className="text-gray-900 dark:text-white font-medium">
                          {selectedOrder.table_number}
                        </span>
                      </div>
                    )}
                    {selectedOrder.payment_method && (
                      <div className="flex justify-between">
                        <span className="text-gray-500 dark:text-gray-400">Payment Method:</span>
                        <span className="text-gray-900 dark:text-white capitalize font-medium">
                          {selectedOrder.is_split_payment ? (
                            <span className="inline-flex items-center gap-1">
                              <span className="px-2 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded text-xs">
                                Split Payment
                              </span>
                            </span>
                          ) : (
                            selectedOrder.payment_method.replace('_', ' ')
                          )}
                        </span>
                      </div>
                    )}
                    {/* Split Payment Details */}
                    {selectedOrder.is_split_payment && selectedOrder.split_payment_details && (
                      <div className="mt-2 p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-700">
                        <p className="text-xs font-medium text-purple-700 dark:text-purple-300 mb-2">
                          Split Payment Breakdown ({selectedOrder.split_payment_details.total_payments} payments)
                        </p>
                        <div className="space-y-1">
                          {selectedOrder.split_payment_details.payments?.map((payment, idx) => (
                            <div key={idx} className="flex justify-between text-xs">
                              <span className="text-gray-600 dark:text-gray-400 capitalize">
                                {payment.payment_method}:
                              </span>
                              <span className="text-gray-900 dark:text-white font-medium">
                                {formatCurrency(payment.amount)}
                                {payment.change_amount && payment.change_amount > 0 && (
                                  <span className="text-gray-500 ml-1">
                                    (change: {formatCurrency(payment.change_amount)})
                                  </span>
                                )}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-gray-500 dark:text-gray-400">Payment Status:</span>
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        selectedOrder.payment_status === 'paid' 
                          ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200'
                          : selectedOrder.payment_status === 'pending'
                          ? 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200'
                          : selectedOrder.payment_status === 'failed'
                          ? 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
                      }`}>
                        {selectedOrder.payment_status === 'paid' && '✓ '}
                        <span className="capitalize">{selectedOrder.payment_status}</span>
                      </span>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-3">Customer Information</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500 dark:text-gray-400">Name:</span>
                      <span className="text-gray-900 dark:text-white">
                        {selectedOrder.customer_name || 'Guest'}
                      </span>
                    </div>
                    {(selectedOrder.customer_phone || selectedOrder.customers?.phone_number) && (
                      <div className="flex justify-between">
                        <span className="text-gray-500 dark:text-gray-400">Phone:</span>
                        <span className="text-gray-900 dark:text-white">
                          {selectedOrder.customer_phone || (selectedOrder.customers as any)?.phone}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Order Notes */}
                {selectedOrder.notes && (
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-3">Order Notes</h4>
                    <div className="text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 rounded-md p-3 border border-gray-200 dark:border-gray-600">
                      {selectedOrder.notes}
                    </div>
                  </div>
                )}
              </div>

              {/* Order Items */}
              <div className="space-y-4">
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-3">Order Items</h4>
                  <div className="space-y-3">
                    {(selectedOrder.order_items_for_modification || selectedOrder.order_items || []).map((item) => (
                      <div key={item.id} className="border-b border-gray-200 dark:border-gray-600 pb-3 last:border-b-0">
                        <div className="flex justify-between items-start mb-1">
                          <div>
                            <h5 className="font-medium text-gray-900 dark:text-white">
                              {formatItemName(item)}
                            </h5>
                            {item.item_description && (
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                {item.item_description}
                              </p>
                            )}
                            {item.variant_name && (
                              <p className="text-sm text-gray-500 dark:text-gray-400">
                                Variant: {item.variant_name}
                                {item.variant_price_modifier > 0 && ` (+${formatCurrency(item.variant_price_modifier)})`}
                              </p>
                            )}
                          </div>
                          <div className="text-right">
                            <p className="font-medium text-gray-900 dark:text-white">
                              {formatCurrency(getOrderItemLineTotal(item))}
                            </p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              Qty: {item.quantity}
                            </p>
                          </div>
                        </div>
                        {item.addons && item.addons.length > 0 && (
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            <span className="font-medium">Addons:</span>
                            <ul className="ml-2 mt-1">
                              {item.addons.map((addon: any, index: number) => (
                                <li key={index} className="flex justify-between">
                                  <span>{addon.name}</span>
                                  {addon.price > 0 && (
                                    <span className="text-gray-600 dark:text-gray-300">
                                      +{formatCurrency(addon.price)}
                                    </span>
                                  )}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {item.notes && (
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            Note: {item.notes}
                          </div>
                        )}
                        {item.kds_stations && (
                          <div className="text-sm">
                            <span className="text-gray-500 dark:text-gray-400">KDS Station: </span>
                            <span 
                              className="font-medium"
                              style={{ color: item.kds_stations.color }}
                            >
                              {item.kds_stations.name}
                            </span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Order Summary — computed from displayed items so totals match the list */}
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-3">Order Summary</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500 dark:text-gray-400">Subtotal (incl. VAT):</span>
                      <span className="text-gray-900 dark:text-white">
                        {formatCurrency(orderModalSummary?.subtotalInclVat ?? selectedOrder.subtotal)}
                      </span>
                    </div>
                    {(orderModalSummary?.discountAmount ?? selectedOrder.discount_amount) > 0 && (
                      <div className="flex justify-between">
                        <span className="text-gray-500 dark:text-gray-400">Discount:</span>
                        <span className="text-green-600 dark:text-green-400">
                          -{formatCurrency(orderModalSummary?.discountAmount ?? selectedOrder.discount_amount)}
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-gray-500 dark:text-gray-400">of which VAT:</span>
                      <span className="text-gray-900 dark:text-white">
                        {formatCurrency(orderModalSummary?.vat ?? selectedOrder.tax_amount)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500 dark:text-gray-400">Service Charge:</span>
                      <span className="text-gray-900 dark:text-white">
                        {formatCurrency(orderModalSummary?.serviceCharge ?? selectedOrder.service_charge)}
                      </span>
                    </div>
                    <div className="flex justify-between font-semibold text-base border-t border-gray-200 dark:border-gray-600 pt-2">
                      <span className="text-gray-900 dark:text-white">Total:</span>
                      <span className="text-gray-900 dark:text-white">
                        {formatCurrency(orderModalSummary?.total ?? selectedOrder.total_amount)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Applied Offers */}
                {/* Regular Offers */}
                {selectedOrder.applied_offers && selectedOrder.applied_offers.length > 0 && (
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-3">Applied Offers</h4>
                    <div className="space-y-2">
                      {selectedOrder.applied_offers.map((offer: any, index: number) => (
                        <div key={index} className="flex justify-between items-center p-2 bg-green-50 dark:bg-green-900/20 rounded-md">
                          <div>
                            <p className="font-medium text-green-800 dark:text-green-200">{offer.name}</p>
                            {offer.description && (
                              <p className="text-sm text-green-600 dark:text-green-300">{offer.description}</p>
                            )}
                          </div>
                          <div className="text-right">
                            {offer.type === 'percentage' && (
                              <span className="text-green-600 dark:text-green-400 font-medium">
                                {offer.value}% off
                              </span>
                            )}
                            {offer.type === 'fixed' && (
                              <span className="text-green-600 dark:text-green-400 font-medium">
                                -{formatCurrency(offer.value)}
                              </span>
                            )}
                            {offer.type === 'combo' && (
                              <span className="text-green-600 dark:text-green-400 font-medium">
                                Combo Deal
                              </span>
                            )}
                            {offer.type === 'bogo' && (
                              <span className="text-green-600 dark:text-green-400 font-medium">
                                BOGO Offer
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Visit-Based Offers */}
                {selectedOrder.applied_visit_based_offers && selectedOrder.applied_visit_based_offers.length > 0 && (
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                      <span className="text-yellow-500">⭐</span>
                      Visit-Based Rewards
                    </h4>
                    <div className="space-y-2">
                      {selectedOrder.applied_visit_based_offers.map((offer: any, index: number) => (
                        <div key={index} className="flex justify-between items-center p-2 bg-orange-50 dark:bg-orange-900/20 rounded-md border border-orange-200 dark:border-orange-700">
                          <div>
                            <p className="font-medium text-orange-800 dark:text-orange-200">{offer.name}</p>
                            {offer.description && (
                              <p className="text-sm text-orange-600 dark:text-orange-300">{offer.description}</p>
                            )}
                            <p className="text-xs text-orange-500 dark:text-orange-400 mt-1">
                              Requires {offer.visit_count} visit{offer.visit_count !== 1 ? 's' : ''}
                            </p>
                          </div>
                          <div className="text-right">
                            {offer.offer_type === 'free_item' && offer.free_item && (
                              <span className="text-orange-600 dark:text-orange-400 font-medium">
                                Free: {offer.free_item.name}
                              </span>
                            )}
                            {offer.offer_type === 'percentage_discount' && (
                              <span className="text-orange-600 dark:text-orange-400 font-medium">
                                {offer.discount_value}% off
                              </span>
                            )}
                            {offer.offer_type === 'fixed_discount' && (
                              <span className="text-orange-600 dark:text-orange-400 font-medium">
                                -{formatCurrency(offer.discount_value)}
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Receipt Print */}
      {receiptData && (
        <div className="receipt-print-area">
          <ThermalReceipt
            order={receiptData}
            restaurantSettings={restaurantSettings}
            receiptLayout={posSettings.receiptLayout}
            receiptLogoUrl={posSettings.receiptLogoUrl}
            printerWidth={posSettings.printerWidth || 80}
            onPrintComplete={() => setReceiptData(null)}
          />
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && orderToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-center mb-4">
              <div className="w-16 h-16 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center">
                <TrashIcon className="h-8 w-8 text-red-600 dark:text-red-400" />
              </div>
            </div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white text-center mb-2">
              Delete Order?
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 text-center mb-6">
              Are you sure you want to delete order <strong>#{orderToDelete.orderNumber}</strong>? 
              This action cannot be undone and will permanently remove the order and all its items.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false)
                  setOrderToDelete(null)
                }}
                disabled={deletingOrderId !== null}
                className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteOrder}
                disabled={deletingOrderId !== null}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {deletingOrderId ? 'Deleting...' : 'Delete Order'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Status Update Modal */}
      {showBulkStatusModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Update Status for {selectedOrders.size} Order{selectedOrders.size !== 1 ? 's' : ''}
              </h3>
              <button
                onClick={() => {
                  setShowBulkStatusModal(false)
                  setBulkStatusValue('')
                }}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                New Status
              </label>
              <select
                value={bulkStatusValue}
                onChange={(e) => setBulkStatusValue(e.target.value)}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="">Select Status</option>
                <option value="pending">Pending</option>
                <option value="confirmed">Confirmed</option>
                <option value="preparing">Preparing</option>
                <option value="ready">Ready</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowBulkStatusModal(false)
                  setBulkStatusValue('')
                }}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={handleBulkStatusUpdate}
                disabled={!bulkStatusValue || updatingBulkStatus}
                className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {updatingBulkStatus ? 'Updating...' : 'Update Status'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Orders

